import cls from 'classnames';
import { useRouter } from 'next/router';
import React, { FC, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';

import CrossIcon from '@/components/icons/icon-cross';
import FirstStep from '@/components/newsletter/components/first-step';
import FourthStep from '@/components/newsletter/components/fourth-step';
import SecondStep from '@/components/newsletter/components/second-step';
import SuccessBlock from '@/components/newsletter/components/success-block';
import ThirdStep from '@/components/newsletter/components/third-step';
import { NewsletterFormInputData } from '@/components/newsletter/lib/newsletter-form-data';
import {
  selectIsNewsletterOpen,
  setIsNewsletterOpen,
} from '@/store/slices/global';
import { selectIsUserLoggedIn, selectUserProfile } from '@/store/slices/user';
import { ImageAsset, NewsletterFormStep } from '@/types/views/generic';
import { NewsletterStaticVariant } from '@/types/views/sections';

import styles from './styles';

type NewsletterComponentFlowStep =
  | 'email_submit'
  | 'name_submit'
  | 'phonenumber_submit'
  | 'submit_error';

const newsletterVariantForDataLayer = (
  variant: NewsletterStaticVariant,
): 'newsletter' | 'event_registration' =>
  variant === 'event' ? 'event_registration' : 'newsletter';

const pushNewsletterComponentDataLayer = (
  variant: NewsletterStaticVariant,
  flowStep: NewsletterComponentFlowStep,
) => {
  window.dataLayer?.push({
    event: 'newsletter_component_view_event',
    newsletter_component_variant: newsletterVariantForDataLayer(variant),
    newsletter_component_flow_step: flowStep,
  });
};

interface NewsletterProps {
  isModal?: boolean;
  background?: ImageAsset;
  newsletterMobileImage?: ImageAsset;
  newsletterDesktopImage?: ImageAsset;
  newsletterSuccessImage?: ImageAsset;
  newsletterErrorImage?: ImageAsset;
  deactivateAdSlots?: boolean;
  newsletterVariant?: NewsletterStaticVariant;
  sectionTitle?: string;
}

const PHONE_REGEX_PATTERN = /^\+[1-9]\d{1,14}$/;

const normalizePhoneNumber = (countryCode: string, phone: string) => {
  const normalizedCountryCode = (countryCode || '+41').trim();
  const digitsOnly = (phone || '').replace(/\D/g, '').replace(/^0+/, '');

  return `${normalizedCountryCode}${digitsOnly}`;
};

const Newsletter: FC<NewsletterProps> = ({
  isModal = true,
  background,
  newsletterMobileImage,
  newsletterDesktopImage,
  newsletterSuccessImage,
  newsletterErrorImage,
  deactivateAdSlots,
  newsletterVariant = 'newsletter',
  sectionTitle,
}) => {
  const [activeStep, setActiveStep] = useState<number>(
    NewsletterFormStep.First,
  );
  const [hasError, setHasError] = useState<boolean>(false);
  const [isAnimated, setIsAnimated] = useState<boolean>(false);
  const [isEmailError, setIsEmailError] = useState<boolean>(false);
  const [isSuccess, setIsSuccess] = useState<boolean>(false);

  const { asPath, replace: routeReplace } = useRouter();

  const dispatch = useDispatch();
  const isUserLoggedIn = useSelector(selectIsUserLoggedIn);
  const profile = useSelector(selectUserProfile);
  const isNewsletterOpen = useSelector(selectIsNewsletterOpen);

  const toggleNewsletterIsOpen = (state: boolean) =>
    dispatch(setIsNewsletterOpen(state));

  const [isAnimatedThirdStep, setIsAnimatedThirdStep] =
    useState<boolean>(false);

  const isFirstStepActive = activeStep === NewsletterFormStep.First;
  const isSecondStepActive = activeStep === NewsletterFormStep.Second;
  const isThirdStepActive = activeStep === NewsletterFormStep.Third;
  const isFourthStepActive = activeStep === NewsletterFormStep.Fourth;

  useEffect(() => {
    if (!isModal) {
      requestAnimationFrame(() => setIsAnimatedThirdStep(isThirdStepActive));
    }
  }, [isThirdStepActive, isModal]);

  useEffect(() => {
    if (isNewsletterOpen) {
      setIsAnimated(true);
    }
  }, [isNewsletterOpen]);

  const {
    handleSubmit,
    register,
    reset,
    formState: { errors, isValid, isSubmitting },
    watch,
  } = useForm<NewsletterFormInputData>({
    mode: 'all',
    defaultValues: {
      email: isUserLoggedIn ? profile?.email : '',
      phone: '',
      countryCode: '+41',
      privacyPolicy: false,
    },
  });

  const { firstName, lastName, phone, countryCode } = watch();

  const closeModal = () => {
    requestAnimationFrame(() => setIsAnimated(false));

    const hashParam = asPath.match(/#([a-zA-Z0-9]+)/gi);

    if (hashParam?.length) {
      const hashParamAction = hashParam[0];
      routeReplace(asPath, asPath.replace(hashParamAction, ''), {
        shallow: true,
      });
    }

    setTimeout(() => {
      toggleNewsletterIsOpen(false);
      setActiveStep(NewsletterFormStep.First);
      reset();
    }, 500);
  };

  const onNextButtonClick = (nextStep: number) => {
    setActiveStep(nextStep);
  };

  const onSaveClick = (nextStep: number) => {
    setHasError(false);
    setIsSuccess(false);

    const submitForm = handleSubmit(
      async (inputData: NewsletterFormInputData) => {
        pushNewsletterComponentDataLayer(
          newsletterVariant,
          newsletterVariant === 'event' ? 'phonenumber_submit' : 'name_submit',
        );

        const formattedPhoneNumber = normalizePhoneNumber(
          inputData.countryCode,
          inputData.phone,
        );

        const { privacyPolicy, ...contactData } = inputData;

        const payload =
          newsletterVariant === 'event'
            ? {
                ...contactData,
                phoneNumber: formattedPhoneNumber,
                newsletterVariant,
              }
            : { ...contactData, newsletterVariant };

        const response = await fetch('/api/newsletter-sendinblue', {
          method: 'post',
          body: JSON.stringify(payload),
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          setHasError(true);

          const res: unknown = await response.json().catch(() => null);
          const isErrorEmail =
            typeof res === 'object' &&
            res !== null &&
            'isErrorEmail' in res &&
            Boolean((res as { isErrorEmail?: unknown }).isErrorEmail);

          if (isErrorEmail) {
            setIsEmailError(true);
          }

          pushNewsletterComponentDataLayer(newsletterVariant, 'submit_error');
        } else {
          setIsSuccess(true);
        }

        onNextButtonClick(nextStep);
      },
    );

    return submitForm();
  };

  const onThirdStepButtonClick = () => {
    setActiveStep(NewsletterFormStep.Second);
  };

  const onThirdStepPrevButtonClick = () => {
    setActiveStep(
      newsletterVariant === 'event'
        ? NewsletterFormStep.Fourth
        : NewsletterFormStep.First,
    );
  };

  const renderContent = () => (
    <div className={styles.wrapper(isModal)}>
      {isModal && <div className={styles.backdrop(isAnimated)} />}

      <div className={cls(styles.content, deactivateAdSlots && 'md:ml-auto')}>
        <form
          className={styles.form(
            isAnimated,
            isModal,
            !isModal && newsletterVariant === 'event',
          )}
          onSubmit={(ev) => ev.preventDefault()}
        >
          {!isModal && isSuccess ? (
            <SuccessBlock
              newsletterSuccessImage={newsletterSuccessImage}
              newsletterVariant={newsletterVariant}
            />
          ) : (
            <>
              {isFirstStepActive && (
                <>
                  {isModal && (
                    <div className={styles.closeIcon} onClick={closeModal}>
                      <CrossIcon />
                    </div>
                  )}

                  <FirstStep
                    register={register}
                    errors={errors}
                    buttonDisabled={isSubmitting || !isValid || !!errors.email}
                    onNextButtonClick={() => {
                      pushNewsletterComponentDataLayer(
                        newsletterVariant,
                        'email_submit',
                      );
                      onNextButtonClick(NewsletterFormStep.Second);
                    }}
                    newsletterMobileImage={newsletterMobileImage}
                    newsletterDesktopImage={newsletterDesktopImage}
                    newsletterVariant={newsletterVariant}
                    sectionTitle={sectionTitle}
                  />
                </>
              )}

              {isSecondStepActive && (
                <SecondStep
                  register={register}
                  errors={errors}
                  buttonDisabled={
                    isSubmitting ||
                    !isValid ||
                    !!errors.firstName ||
                    !!errors.lastName ||
                    !!errors.privacyPolicy
                  }
                  onPrevButtonClick={() =>
                    setActiveStep(NewsletterFormStep.First)
                  }
                  onNextButtonClick={() => {
                    if (newsletterVariant === 'event') {
                      pushNewsletterComponentDataLayer(
                        newsletterVariant,
                        'name_submit',
                      );
                      onNextButtonClick(NewsletterFormStep.Fourth);
                    } else {
                      onSaveClick(NewsletterFormStep.Third);
                    }
                  }}
                  inputValues={{ firstName, lastName }}
                  newsletterVariant={newsletterVariant}
                />
              )}

              {isFourthStepActive && newsletterVariant === 'event' && (
                <FourthStep
                  register={register}
                  errors={errors}
                  buttonDisabled={
                    isSubmitting ||
                    !isValid ||
                    !!errors.phone ||
                    !!errors.countryCode ||
                    !PHONE_REGEX_PATTERN.test(
                      normalizePhoneNumber(countryCode, phone),
                    )
                  }
                  onPrevButtonClick={() =>
                    setActiveStep(NewsletterFormStep.Second)
                  }
                  onNextButtonClick={() =>
                    onSaveClick(NewsletterFormStep.Third)
                  }
                  inputValues={{ phone, countryCode }}
                  newsletterVariant={newsletterVariant}
                />
              )}
            </>
          )}

          {isThirdStepActive &&
            (isModal ? (
              <ThirdStep
                onButtonClick={onThirdStepButtonClick}
                onPrevButtonClick={onThirdStepPrevButtonClick}
                hasError={hasError}
                emailExists={isEmailError}
                newsletterErrorImage={newsletterErrorImage}
                newsletterSuccessImage={newsletterSuccessImage}
                newsletterVariant={newsletterVariant}
              />
            ) : (
              <div className={styles.wrapper()}>
                <div className={styles.backdrop(isAnimatedThirdStep)} />

                <div className={styles.content}>
                  <div className={styles.form(isAnimatedThirdStep)}>
                    <ThirdStep
                      onButtonClick={() =>
                        setActiveStep(NewsletterFormStep.Second)
                      }
                      onPrevButtonClick={onThirdStepPrevButtonClick}
                      hasError={hasError}
                      emailExists={isEmailError}
                      newsletterSuccessImage={newsletterSuccessImage}
                      newsletterErrorImage={newsletterErrorImage}
                      newsletterVariant={newsletterVariant}
                    />
                  </div>
                </div>
              </div>
            ))}
        </form>
      </div>
    </div>
  );

  const isEventBackgroundVisible =
    !isModal && newsletterVariant === 'event' && !!background?.src;

  const content = renderContent();

  const contentWithEventBackground = isEventBackgroundVisible ? (
    <div
      className={styles.eventBackgroundWrapper}
      style={{ backgroundImage: `url(${background?.src})` }}
    >
      <div className={styles.eventBackgroundInner}>{content}</div>
    </div>
  ) : (
    content
  );

  return isModal && typeof document !== 'undefined'
    ? ReactDOM.createPortal(
        renderContent(),
        document.querySelector('#modals') as Element,
      )
    : contentWithEventBackground;
};

export default Newsletter;
